import { BookOpenText, CircleAlert } from "lucide-react";
import { interpretClinicalTexts } from "@/domain/clinicalTextInterpreter";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";

function severityVariant(severity: string): "destructive" | "default" | "secondary" | "outline" {
  if (severity === "urgente") return "destructive";
  if (severity === "relevante") return "default";
  if (severity === "cautela") return "secondary";
  return "outline";
}

export function ClinicalTextInterpretationPanel({ patient, sessions = [], checkins = [], doses = [] }: { patient?: any; sessions?: any[]; checkins?: any[]; doses?: any[] }) {
  const interpretation = interpretClinicalTexts({ patient, sessions, checkins, doses });
  const positiveSignals = interpretation.signals.filter((signal) => signal.positiveEvidence.length > 0);
  const negatedOnly = interpretation.signals.filter((signal) => !signal.positiveEvidence.length && signal.negatedEvidence.length);

  return (
    <Card className="min-w-0 space-y-4 overflow-hidden p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2 font-medium">
            <BookOpenText className="h-4 w-4 text-primary" /> Interpretação dos textos clínicos
          </div>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
            Lê queixa, história, baseline, sessões, check-ins e textos ligados às doses. Cada hipótese mantém a fonte, reconhece negações e exige validação médica.
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          <Badge variant="outline">cobertura {interpretation.textCoverageScore}/100</Badge>
          <Badge variant="secondary">{interpretation.sourcesRead.length} fonte(s)</Badge>
        </div>
      </div>

      {positiveSignals.length ? (
        <div className="grid min-w-0 grid-cols-1 gap-3 xl:grid-cols-2">
          {positiveSignals.slice(0, 12).map((signal) => (
            <div key={signal.id} className="min-w-0 space-y-3 rounded-xl border p-3">
              <div className="flex min-w-0 flex-wrap items-start justify-between gap-2">
                <div className="min-w-0 break-words text-sm font-medium">{signal.label}</div>
                <div className="flex shrink-0 gap-1">
                  <Badge variant={severityVariant(signal.severity)}>{signal.severity}</Badge>
                  <Badge variant="outline">{signal.confidence}</Badge>
                </div>
              </div>
              <p className="text-xs leading-relaxed">{signal.hypothesis}</p>
              <div className="rounded-lg bg-muted/40 p-2.5 text-xs">
                <span className="font-medium">Estratégia de manejo/revisão:</span> {signal.management}
              </div>
              <div className="space-y-1.5">
                <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Evidências textuais</div>
                {signal.positiveEvidence.slice(0, 3).map((evidence, index) => (
                  <div key={`${evidence.sourceId}-${index}`} className="rounded-lg border-l-2 border-primary bg-muted/20 px-2.5 py-2 text-[11px] leading-relaxed">
                    <div className="font-medium text-muted-foreground">{evidence.sourceLabel}</div>
                    <div>“{evidence.excerpt}”</div>
                  </div>
                ))}
              </div>
              {signal.contradiction ? (
                <div className="flex items-start gap-1.5 text-[11px] text-warning">
                  <CircleAlert className="mt-0.5 h-3 w-3 shrink-0" />
                  <span>Há menções positivas e negadas em fontes diferentes. Revisar temporalidade e contexto.</span>
                </div>
              ) : null}
              <div className="text-[11px] text-muted-foreground">
                <span className="font-medium">Validar:</span> {signal.validation.join("; ")}.
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-sm text-muted-foreground">Nenhum tema positivo foi extraído dos textos disponíveis. Isso pode significar ausência de texto, linguagem não reconhecida ou achados realmente ausentes.</div>
      )}

      {negatedOnly.length ? (
        <details className="rounded-xl border p-3">
          <summary className="cursor-pointer text-sm font-medium">Achados mencionados apenas como negados/ausentes ({negatedOnly.length})</summary>
          <div className="mt-3 space-y-2">
            {negatedOnly.map((signal) => (
              <div key={signal.id} className="rounded-lg bg-muted/30 p-2 text-xs">
                <div className="font-medium">{signal.label}</div>
                <div className="text-muted-foreground">{signal.negatedEvidence[0]?.excerpt}</div>
              </div>
            ))}
          </div>
        </details>
      ) : null}

      <div className="rounded-xl border bg-muted/20 p-3 text-xs text-muted-foreground">
        <span className="font-medium">Limitações:</span> {interpretation.limitations.join("; ")}.
      </div>
    </Card>
  );
}
